import { Button as ButtonBase, ProgressBar, TooltipTrigger } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { styled } from "../../../../styled-system/jsx";
import { Tooltip } from "./Tooltip";
import type { ComponentProps, ReactNode } from "react";
import { ImSpinner2 } from "react-icons/im";
import { css, cva } from "../../../../styled-system/css";

const commonButtonStyles = css.raw({
  justifyContent: "center",
  border: "1px solid",
  borderRadius: "button",
  minHeight: "button",
  paddingY: "small",
  paddingX: "medium",
  fontSize: "xsmall",
  fontWeight: "700",
  letterSpacing: "2.5px",
  lineHeight: "normal",
  textTransform: "uppercase",
  _disabled: {
    cursor: "not-allowed",
    opacity: 0.5,
  },
});

export const buttonRecipe = cva({
  base: {
    all: "unset",
    boxSizing: "border-box",
    cursor: "pointer",
    display: "flex",
    gap: "xsmall",
    alignItems: "center",
    textWrap: "nowrap",
    transition: "all 0.3s ease 0s",
    flexWrap: "nowrap",
    _focusVisible: {
      outline: "2px solid {colors.bootstrapBlue}",
    },
    "&[data-pending]": {
      opacity: 0.75,
    },
  },
  variants: {
    variant: {
      link: {
        color: "colorPalette",
        textDecoration: "underline",
        fontWeight: "600",
        _disabled: {
          cursor: "default",
          color: "grey",
        },
        _hover: {
          opacity: 0.8,
          _disabled: {
            opacity: 1,
          },
        },
      },
      primary: {
        ...commonButtonStyles,
        borderColor: "colorPalette",
        color: "white",
        backgroundColor: "colorPalette",
        _hover: {
          borderColor: "colorPalette",
          backgroundColor: "colorPalette",
          opacity: 0.9,
          _disabled: {
            borderColor: "colorPalette",
            backgroundColor: "colorPalette",
            opacity: 0.5,
          },
        },
      },
      secondary: {
        ...commonButtonStyles,
        borderColor: "colorPalette",
        color: "colorPalette",
        backgroundColor: "white",
        _hover: {
          borderColor: "colorPalette",
          backgroundColor: "colorPalette/10",
          _disabled: {
            backgroundColor: "white",
            opacity: 0.5,
          },
        },
      },
      contentOnly: {
        border: "none",
        backgroundColor: "transparent",
        _disabled: {
          cursor: "default",
          color: "grey",
          _hover: {
            backgroundColor: "transparent",
          },
        },
        _hover: {
          backgroundColor: "lightgrey",
        },
      },
      icon: {
        padding: "small",
        borderRadius: "50%",
        border: "none",
        backgroundColor: "transparent",
        justifyContent: "center",
        display: "inline-flex",
        _hover: {
          backgroundColor: "lightgrey",
        },
        _disabled: {
          cursor: "default",
          color: "grey",
          _hover: {
            backgroundColor: "transparent",
          },
        },
      },
      flat: {
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        border: "1px solid",
        borderColor: "lightgrey",
        borderRadius: "light",
        width: "100%",
        paddingY: "medium",
        paddingX: "medium",
        backgroundColor: "gray.100",
        transition: "all 0.3s ease 0s",
        _disabled: {
          cursor: "not-allowed",
          borderColor: "gray.300",
          backgroundColor: "gray.200",
          opacity: 0.7,
        },
        _hover: {
          backgroundColor: "gray.200",
        },
      },
    },
    colorScheme: {
      fuchsia: {
        colorPalette: "fuchsia",
      },
      mauve: {
        colorPalette: "mauve",
      },
      destructive: {
        colorPalette: "error",
      },
    },
    size: {
      small: {
        borderRadius: "8px",
        minHeight: "unset",
        paddingY: "xsmall",
        paddingX: "small",
        fontWeight: "600",
        letterSpacing: "0.7px",
      },
    },
  },
  defaultVariants: {
    variant: "primary",
    colorScheme: "fuchsia",
  },
});

const StyledButton = styled(ButtonBase, buttonRecipe);

type ButtonProps = ComponentProps<typeof StyledButton> & {
  children: ReactNode;
  tooltip?: string | ReactNode;
  tooltipPlacement?: "top" | "bottom" | "left" | "right";
};

export function Button({ children, tooltip, tooltipPlacement = "top", ...props }: ButtonProps) {
  const { t } = useTranslation(["common"]);
  const content = (
    <StyledButton {...props}>
      {({ isPending }) => {
        return (
          <>
            {children}
            {isPending && (
              <ProgressBar aria-label={t(($) => $.common["Saving..."])} isIndeterminate>
                <ImSpinner2
                  className={css({
                    animation: "spin",
                  })}
                />
              </ProgressBar>
            )}
          </>
        );
      }}
    </StyledButton>
  );

  if (tooltip) {
    return (
      <TooltipTrigger delay={0}>
        {content}
        <Tooltip placement={tooltipPlacement} offset={10}>
          {tooltip}
        </Tooltip>
      </TooltipTrigger>
    );
  }

  return content;
}

export const LinkButton = (props: ComponentProps<typeof Button>) => <Button variant="link" {...props} />;
